/* istanbul ignore file */
/**
 * Created by rathompson on 11/17/21
 * File: AssignmentOld
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  Bold,
  Button,
  Tooltip,
  noop,
  Body,
  Popover,
  CardBody,
  Input,
} from '@panwds/react-ui'
import { Select } from '@panwds/react-form'
import { DeleteIcon, InfoIcon, LockIcon } from '@panwds/icons'
import {
  LABEL_APPS_SERVICES,
  LABEL_MORE_INFO,
  LABEL_ROlE,
  LABEL_SCOPE,
  PLACEHOLDER_ADD_ACCESS,
  PLACEHOLDER_SELECT_ROLE,
  TOOLTIP_INHERITED_DELETE,
} from '../../../constants/LabelConstants'
import { useFormContext } from 'react-hook-form'
import { createUrnUtil } from '@sparky/framework'
import { getVarValue } from '../../../../common/utils/Sparky'
import {
  FF_DISPLAY_SCOPE_COLUMN,
  FF_ROLE_INFO_ENABLED,
} from '../../../../common/utils/FeatureFlags'
import ZIndex from '../../../../common/components/zIndex/ZIndex'
import GlobalContext from '../../../../common/context/GlobalContext'
import styled from 'styled-components'
import useReadOnly from '../../../../common/hooks/useReadOnly'
import { IDENTITIES_PERMISSION_SET } from '../../../constants/IAMConstants'

const CONTAINER = styled.div`
  label {
    margin: 0;
    .tw-block {
      margin: 0;
    }
  }
  button {
    margin: 0;
  }
`

const AppName = createUrnUtil('app', {
  components: [
    'app_id',
    'index'
  ],
  separator: ':',
  allowEmpty: false
})

const RoleName = createUrnUtil('role', {
  components: [
    'app_id',
    'index',
    'role'
  ],
  separator: ':',
  allowEmpty: false
})

const roleInfoEnabled = getVarValue(FF_ROLE_INFO_ENABLED, false)
const displayScopeColumn = getVarValue(FF_DISPLAY_SCOPE_COLUMN, false)

const AssignmentOld = ({ app, appItems, roleItems, onAppChange, onRoleChange, onRemove, optional, onRoleInfoClick: _onRoleInfoClick }) => {

  const { iamReducer, treeReducer } = useContext(GlobalContext)
  const { roleMap: _roleMap, customRoleMap } = iamReducer
  const { selectedTreeNode = {} } = treeReducer
  const { setValue, unregister } = useFormContext()

  const [popOverOpen, setPopOverOpen] = useState(false)
  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)

  const roleMap = useMemo(() => {
    return { ..._roleMap, ...customRoleMap }
  }, [_roleMap, customRoleMap])

  const appName = useMemo(() => {
    return AppName.format({
      app_id: app?.id,
      index: String(app?.index)
    })
  }, [app])

  const roleName = useMemo(() => {
    return RoleName.format({
      app_id: app?.id,
      index: String(app?.index),
      role: 'role'
    })
  }, [app])

  useEffect(() => {
    setValue(appName, app)
    setValue(roleName, app?.role)
    return () => {
      unregister(appName)
      unregister(roleName)
    }
  }, [appName, roleName, unregister, setValue, app])

  const appSelect = useMemo(() => (
    <Select
      addClassName='cmm-modal-select'
      name={appName}
      label={LABEL_APPS_SERVICES}
      showLabel={false}
      items={appItems}
      fitToItemWidth
      disabled={app?.disabled || readOnly}
      selectedItem={app}
      register={{
        required: !optional,
        onChange: (change) => {
          onAppChange(app?.index, change.target.value)
        }
      }}
      button={
        <Button
          addClassName='tw-justify-between tw-mt-2'
          isMenu
          fullWidth
          appearance='tertiary'
        >
          {app?.name || PLACEHOLDER_ADD_ACCESS}
        </Button>
      }
    />
  ), [appName, appItems, app, onAppChange, optional, readOnly])

  const onRoleInfoClick = useCallback(() => {
    setPopOverOpen(false)
    if (app?.role && _onRoleInfoClick && typeof _onRoleInfoClick === 'function') {
      _onRoleInfoClick(app?.role)
    }
  }, [_onRoleInfoClick, app?.role])

  const description = useMemo(() => {
    return roleMap[app?.role?.name]?.description
  }, [app?.role?.name, roleMap])

  const roleSelect = useMemo(() => (
    <>
      <div style={{ flex: '1 1 0', width: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className='tw-flex tw-gap-2 tw-items-center tw-flex-1'>
          <Bold appearance='semibold' >{LABEL_ROlE}</Bold>
          {roleInfoEnabled && app?.role && (
            <Popover
              addClassName='cmm-modal-select'
              isOpen={popOverOpen}
              onIsOpenChange={() => setPopOverOpen(!popOverOpen)}
              button={(
                <div>
                  <Button
                    aria-label='role-details'
                    icon={(
                      <InfoIcon size='sm' className='cm-text-icon-gray' />
                    )}
                    appearance="clear"
                    style={{ background: 'transparent', padding: 0 }}
                  />
                </div>
              )}
              label="Role Details"
              showLabel={false}
              defaultPlacement="bottom-start"
            >
              <CardBody>
                <div style={{ maxWidth: '20rem' }}>
                  <Body>{description}</Body>
                  <div className='tw-flex tw-justify-end'>
                    <Body onClick={onRoleInfoClick} appearance='link' size='sm' addClassName='tw-cursor-pointer hover:tw-underline tw-pt-4'>{LABEL_MORE_INFO}</Body>
                  </div>
                </div>
              </CardBody>
            </Popover>
          )}
        </div>
        <div className='tw-flex-1'>
          <Select
            addClassName='cmm-modal-select'
            name={roleName}
            label={LABEL_ROlE}
            showLabel={false}
            items={roleItems}
            fitToItemWidth
            disabled={!app?.name || app?.disabled || readOnly}
            selectedItem={app?.role}
            register={{
              required: !optional,
              onChange: (change) => {
                onRoleChange(app, change.target.value)
              }
            }}
            button={
              <Button
                addClassName='tw-justify-between tw-mt-2'
                isMenu
                fullWidth
                appearance='tertiary'
              >
                {app?.role?.value || PLACEHOLDER_SELECT_ROLE}
              </Button>
            }
          />
        </div>
      </div>
      {!!app.policy?.resource?.resource_scope && displayScopeColumn && (
        <div style={{ flex: '1 1 0', width: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className='tw-flex-1'>
            <Bold appearance='semibold'>{LABEL_SCOPE}</Bold>
          </div>
          <div className='tw-flex-1'>
            <Input
              disabled
              value={app.policy?.resource?.resource_scope}
              addClassName='tw-mt-2'
              readOnly
            />
          </div>
        </div>
      )}
      <div
        className={(app.policy?.resource?.resource_scope && displayScopeColumn) ? '' : 'tw-pr-8'}
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end' }}
      >
        {!app.policy?.resource?.resource_scope && app?.name && app?.disabled ? (
          <>
            <Tooltip label={TOOLTIP_INHERITED_DELETE}>
              <div className='tw-flex tw-justify-center tw-items-center tw-grayscale tw-text-gray-400 tw-cursor-not-allowed' style={{ height: '2rem', width: '2rem' }}>
                <LockIcon size='sm' className='cm-text-gray-400' />
              </div>
            </Tooltip>
          </>
        ) : app.policy?.resource?.resource_scope ? <></> : (
          <Button
            aria-label='role-trash'
            appearance='clear'
            style={{ background: 'transparent' }}
            icon={(
              <DeleteIcon size='sm' className='cm-text-gray-400' />
            )}
            onClick={onRemove}
            disabled={app?.disabled || readOnly}
          />
        )}
      </div>
    </>
  ), [app, popOverOpen, description, onRoleInfoClick, onRemove, roleName, roleItems, optional, onRoleChange, readOnly])

  return (
    <CONTAINER className="tw-mt-2 tw-flex tw-flex-row tw-gap-2">
      <div style={{ flex: '1 1 0', width: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className='tw-flex-1'>
          <Bold appearance='semibold'>{LABEL_APPS_SERVICES}</Bold>
        </div>
        <div className='tw-flex-1'>
          {appSelect}
        </div>
      </div>
      <ZIndex className='tw-flex tw-flex-row tw-flex-1 tw-gap-2'>
        {roleSelect}
      </ZIndex>
    </CONTAINER>
  )
}

AssignmentOld.propTypes = {}

AssignmentOld.defaultProps = {
  app: {},
  appItems: [],
  roleItems: [],
  onAppChange: noop,
  onRoleChange: noop,
  onRemove: noop
}
export default AssignmentOld
